import Router from 'next/router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import type { NavigationContract, NavigationGuard, NavigateOptions } from '@shell/contracts'

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

// Clear only overflow-related scroll-lock styles that modal/overlay libraries leave on
// body after a route change. Removing the entire class or style attribute is too broad
// and would destroy unrelated styles set by concurrent modals or toasts.
const removeBodyScrollLock = () => {
  const body = document.body
  if (!body) return
  body.style.overflow = ''
  body.style.overflowY = ''
}

// ---------------------------------------------------------------------------
// NextRoutingAdapter — implements NavigationContract using next/router
// ---------------------------------------------------------------------------

class NextRoutingAdapter implements NavigationContract {
  private _guards = new Set<NavigationGuard>()
  private _listenersRegistered = false

  /**
   * Bootstrap NProgress and body-cleanup listeners on Router.events.
   *
   * Called from PageLayout.componentDidMount (not in the constructor) to
   * avoid SSR contamination of the singleton — Router.events are only
   * available client-side.
   */
  setupListeners(): void {
    if (this._listenersRegistered || typeof window === 'undefined') return

    NProgress.configure({ showSpinner: false })

    Router.events.on('routeChangeStart', () => NProgress.start())
    Router.events.on('routeChangeError', () => NProgress.done())
    Router.events.on('routeChangeComplete', () => {
      // Clear scroll-lock left by modals/overlays on the previous page.
      // Runs on complete (not start) so concurrent modals during transition are not disrupted.
      removeBodyScrollLock()
      NProgress.done()
    })

    this._listenersRegistered = true
  }

  async navigate(path: string, options: NavigateOptions = {}): Promise<void> {
    const allowed = await this._runGuards(path)
    if (!allowed) return

    const { shallow = false, displayAs, query } = options
    // When query params are provided, pass an object so Next.js merges them
    // correctly instead of requiring manual URL serialization.
    const url = query ? { pathname: path, query } : path
    await Router.push(url, displayAs, { shallow })
  }

  async replace(path: string, options: Omit<NavigateOptions, 'replace'> = {}): Promise<void> {
    const allowed = await this._runGuards(path)
    if (!allowed) return

    const { shallow = false, displayAs, query } = options
    const url = query ? { pathname: path, query } : path
    await Router.replace(url, displayAs, { shallow })
  }

  async back(): Promise<void> {
    // 'back' is a sentinel value — guards receive it as the target path.
    // This covers the BackButton case: if isFormDirty, the guard shows
    // the UnsavedModal before Router.back() fires.
    const allowed = await this._runGuards('back')
    if (!allowed) return

    Router.back()
  }

  registerGuard(guard: NavigationGuard): () => void {
    this._guards.add(guard)
    return () => this._guards.delete(guard)
  }

  onRouteComplete(cb: () => void): () => void {
    Router.events.on('routeChangeComplete', cb)
    return () => Router.events.off('routeChangeComplete', cb)
  }

  getCurrentRoute(): string {
    if (typeof window === 'undefined') return '/'
    // asPath includes query string and hash (e.g. /foo?bar=1), matching
    // the previous Router.router?.asPath behavior in WithPortalAuthorization.
    return Router.asPath ?? Router.pathname
  }

  getQuery(): Record<string, string | string[] | undefined> {
    if (typeof window === 'undefined') return {}
    return Router.query as Record<string, string | string[] | undefined>
  }

  private async _runGuards(path: string): Promise<boolean> {
    for (const guard of this._guards) {
      try {
        const result = await guard(path)
        if (!result) return false
      } catch (err) {
        // Fail closed: a guard that throws must not silently discard unsaved changes.
        // Block navigation and surface the error so it can be fixed.
        console.error('[NextRoutingAdapter] Navigation guard threw — blocking navigation:', err)
        return false
      }
    }
    return true
  }
}

// ---------------------------------------------------------------------------
// Singleton export — follows the same pattern as auth and storage adapters
// ---------------------------------------------------------------------------

let instance: NextRoutingAdapter | null = null

/**
 * Returns the singleton NextRoutingAdapter instance.
 *
 * Note: the return type is the concrete class (not NavigationContract) so that
 * PageLayout can call setupListeners() — a bootstrap method not on the contract.
 * All other consumers should type their reference as NavigationContract.
 */
export const getRoutingAdapter = (): NextRoutingAdapter => {
  if (!instance) instance = new NextRoutingAdapter()
  return instance
}
